import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Table, X, Edit3, Check, Loader2, Grid3X3, Calculator } from 'lucide-react';
import clsx from 'clsx';
import type { Spreadsheet } from '@/types';
import { SpreadsheetToolbar } from '@/components/spreadsheet/SpreadsheetToolbar';
import { updateSpreadsheet } from '@/db/projects';
import { useAppStore } from '@/store/appStore';
import { handleError } from '@/utils/errorHandler';
import {
  getCellId,
  indexToColumn,
  isFormula,
  getDisplayValue,
  type CellValue,
  type GridData,
} from '@/utils/spreadsheetFormulas';

interface SpreadsheetViewProps {
  spreadsheet: Spreadsheet;
  onUpdate?: (id: string, updates: Partial<Spreadsheet>) => Promise<void>;
  onClose?: () => void;
}

interface Selection {
  row: number | null;
  col: number | null;
}

interface UndoState {
  data: GridData;
  rows: number;
  cols: number;
}

const DEFAULT_ROWS = 20;
const DEFAULT_COLS = 10;

export function SpreadsheetView({ spreadsheet, onUpdate, onClose }: SpreadsheetViewProps) {
  const updateCurrentItem = useAppStore((state) => state.updateCurrentItem);
  // Parse initial data
  const initialData = useMemo(() => {
  const content = spreadsheet.content as Record<string, unknown> | undefined;
  return {
      cells: (content?.cells as GridData) || {},
      rows: (content?.rows as number) || DEFAULT_ROWS,
      cols: (content?.cols as number) || DEFAULT_COLS,
  };
  }, [spreadsheet.content]);

  const [gridData, setGridData] = useState<GridData>(initialData.cells);
  const [rows, setRows] = useState(initialData.rows);
  const [cols, setCols] = useState(initialData.cols);
  const [selection, setSelection] = useState<Selection>({ row: null, col: null });
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [undoStack, setUndoStack] = useState<UndoState[]>([]);
  const [redoStack, setRedoStack] = useState<UndoState[]>([]);
  const [clipboard, setClipboard] = useState<CellValue>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [title, setTitle] = useState(spreadsheet.name);

  const tableRef = useRef<HTMLDivElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasUnsavedChangesRef = useRef(false);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChangesRef.current) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Sync with prop changes (e.g., when spreadsheet is updated externally)
  useEffect(() => {
  const content = spreadsheet.content as Record<string, unknown> | undefined;
  const newCells = (content?.cells as GridData) || {};
  const newRows = (content?.rows as number) || DEFAULT_ROWS;
  const newCols = (content?.cols as number) || DEFAULT_COLS;

  setGridData(prevCells => {
      // Only update if data actually changed to avoid unnecessary re-renders
      if (JSON.stringify(newCells) !== JSON.stringify(prevCells)) {
    return newCells;
      }
      return prevCells;
  });
  setRows(prevRows => newRows !== prevRows ? newRows : prevRows);
  setCols(prevCols => newCols !== prevCols ? newCols : prevCols);
  }, [spreadsheet.content]);

  // Sync title with prop changes
  useEffect(() => {
  setTitle(spreadsheet.name);
  }, [spreadsheet.name]);

  // Auto-save with debounce
  const saveData = useCallback(async () => {
  setIsSaving(true);
  try {
      const content = { cells: gridData, rows, cols };
      const updated = await updateSpreadsheet(spreadsheet.id, { content });
      if (updated) {
    // Update store if item is current
    updateCurrentItem({ content });
    if (onUpdate) {
          await onUpdate(spreadsheet.id, { content });
    }
      }
      hasUnsavedChangesRef.current = false;
      setLastSaved(new Date());
  } catch (error) {
      handleError(error, { level: 'error', message: 'Error saving spreadsheet' });
  } finally {
      setIsSaving(false);
  }
  }, [gridData, rows, cols, spreadsheet.id, onUpdate, updateCurrentItem]);

  // Debounced save
  useEffect(() => {
  hasUnsavedChangesRef.current = true;
  if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
  }
  saveTimeoutRef.current = setTimeout(saveData, 2000);
  return () => {
      if (saveTimeoutRef.current) {
    clearTimeout(saveTimeoutRef.current);
      }
  };
  }, [gridData, rows, cols, saveData]);

  // Focus title input when editing
  useEffect(() => {
  if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
  }
  }, [isEditingTitle]);

  // Save state for undo
  const pushUndo = useCallback(() => {
  setUndoStack(prev => [...prev.slice(-50), { data: { ...gridData }, rows, cols }]);
  setRedoStack([]);
  }, [gridData, rows, cols]);

  // Handle cell value change
  const updateCell = useCallback((row: number, col: number, value: string) => {
  pushUndo();
  const cellId = getCellId(row, col);
  setGridData(prev => ({
      ...prev,
      [cellId]: value || null,
  }));
  }, [pushUndo]);

  // Handle cell click
  const handleCellClick = useCallback((row: number, col: number) => {
  if (editingCell) {
      // Save current edit first
      const [editRow, editCol] = editingCell.split('-').map(Number);
      updateCell(editRow, editCol, editValue);
      setEditingCell(null);
  }
  setSelection({ row, col });
  }, [editingCell, editValue, updateCell]);

  // Handle cell double-click to edit
  const handleCellDoubleClick = useCallback((row: number, col: number) => {
  const cellId = getCellId(row, col);
  const value = gridData[cellId];
  setEditValue(value !== null && value !== undefined ? String(value) : '');
  setEditingCell(cellId);
  setSelection({ row, col });
  }, [gridData]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
  if (editingCell) {
      if (e.key === 'Enter') {
    e.preventDefault();
    const [row, col] = editingCell.split('-').map(Number);
    updateCell(row, col, editValue);
    setEditingCell(null);
    setSelection({ row: row + 1, col });
      } else if (e.key === 'Escape') {
    setEditingCell(null);
    setEditValue('');
      } else if (e.key === 'Tab') {
    e.preventDefault();
    const [row, col] = editingCell.split('-').map(Number);
    updateCell(row, col, editValue);
    setEditingCell(null);
    if (e.shiftKey) {
          setSelection({ row, col: Math.max(0, col - 1) });
    } else {
          setSelection({ row, col: Math.min(cols - 1, col + 1) });
    }
      }
      return;
  }

  const { row, col } = selection;
  if (row === null || col === null) return;

  switch (e.key) {
      case 'ArrowUp':
    e.preventDefault();
    setSelection({ row: Math.max(0, row - 1), col });
    break;
      case 'ArrowDown':
    e.preventDefault();
    setSelection({ row: Math.min(rows - 1, row + 1), col });
    break;
      case 'ArrowLeft':
    e.preventDefault();
    setSelection({ row, col: Math.max(0, col - 1) });
    break;
      case 'ArrowRight':
    e.preventDefault();
    setSelection({ row, col: Math.min(cols - 1, col + 1) });
    break;
      case 'Enter':
    e.preventDefault();
    handleCellDoubleClick(row, col);
    break;
      case 'Delete':
      case 'Backspace':
    e.preventDefault();
    updateCell(row, col, '');
    break;
      default:
    // Start editing if printable character
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
          setEditValue(e.key);
          setEditingCell(getCellId(row, col));
    }
  }
  }, [editingCell, editValue, selection, rows, cols, updateCell, handleCellDoubleClick]);

  // Toolbar actions
  const handleAddRow = useCallback(() => {
  pushUndo();
  setRows(prev => prev + 1);
  }, [pushUndo]);

  const handleAddColumn = useCallback(() => {
  pushUndo();
  setCols(prev => prev + 1);
  }, [pushUndo]);

  const handleDeleteRow = useCallback(() => {
  if (selection.row === null || rows <= 1) return;
  pushUndo();
  const rowToDelete = selection.row;

  // Shift all cells up
  const newData: GridData = {};
  Object.entries(gridData).forEach(([cellId, value]) => {
      const [r, c] = cellId.split('-').map(Number);
      if (r < rowToDelete) {
    newData[cellId] = value;
      } else if (r > rowToDelete) {
    newData[getCellId(r - 1, c)] = value;
      }
  });

  setGridData(newData);
  setRows(prev => prev - 1);
  setSelection({ row: null, col: null });
  }, [selection.row, rows, gridData, pushUndo]);

  const handleDeleteColumn = useCallback(() => {
  if (selection.col === null || cols <= 1) return;
  pushUndo();
  const colToDelete = selection.col;

  // Shift all cells left
  const newData: GridData = {};
  Object.entries(gridData).forEach(([cellId, value]) => {
      const [r, c] = cellId.split('-').map(Number);
      if (c < colToDelete) {
    newData[cellId] = value;
      } else if (c > colToDelete) {
    newData[getCellId(r, c - 1)] = value;
      }
  });

  setGridData(newData);
  setCols(prev => prev - 1);
  setSelection({ row: null, col: null });
  }, [selection.col, cols, gridData, pushUndo]);

  const handleSort = useCallback((ascending: boolean) => {
  if (selection.col === null) return;
  pushUndo();

  const col = selection.col;

  // Get all row data for sorting
  const rowsData: { row: number; value: CellValue }[] = [];
  for (let r = 0; r < rows; r++) {
      rowsData.push({ row: r, value: gridData[getCellId(r, col)] ?? null });
  }

  // Sort rows
  rowsData.sort((a, b) => {
      const aVal = a.value ?? '';
      const bVal = b.value ?? '';

      // Try numeric comparison first
      const aNum = parseFloat(String(aVal));
      const bNum = parseFloat(String(bVal));
      if (!isNaN(aNum) && !isNaN(bNum)) {
    return ascending ? aNum - bNum : bNum - aNum;
      }

      // Fall back to string comparison
      const comparison = String(aVal).localeCompare(String(bVal));
      return ascending ? comparison : -comparison;
  });

  // Rebuild grid data with sorted rows
  const newData: GridData = {};
  rowsData.forEach((item, newRow) => {
      for (let c = 0; c < cols; c++) {
    const oldCellId = getCellId(item.row, c);
    const newCellId = getCellId(newRow, c);
    if (gridData[oldCellId] !== undefined) {
          newData[newCellId] = gridData[oldCellId];
    }
      }
  });

  setGridData(newData);
  }, [selection.col, rows, cols, gridData, pushUndo]);

  const handleCopy = useCallback(() => {
  if (selection.row !== null && selection.col !== null) {
      const cellId = getCellId(selection.row, selection.col);
      setClipboard(gridData[cellId] ?? null);
  }
  }, [selection, gridData]);

  const handlePaste = useCallback(() => {
  if (selection.row !== null && selection.col !== null && clipboard !== null) {
      updateCell(selection.row, selection.col, String(clipboard));
  }
  }, [selection, clipboard, updateCell]);

  const handleUndo = useCallback(() => {
  if (undoStack.length === 0) return;

  const lastState = undoStack[undoStack.length - 1];
  setRedoStack(prev => [...prev, { data: { ...gridData }, rows, cols }]);
  setUndoStack(prev => prev.slice(0, -1));
  setGridData(lastState.data);
  setRows(lastState.rows);
  setCols(lastState.cols);
  }, [undoStack, gridData, rows, cols]);

  const handleRedo = useCallback(() => {
  if (redoStack.length === 0) return;

  const nextState = redoStack[redoStack.length - 1];
  setUndoStack(prev => [...prev, { data: { ...gridData }, rows, cols }]);
  setRedoStack(prev => prev.slice(0, -1));
  setGridData(nextState.data);
  setRows(nextState.rows);
  setCols(nextState.cols);
  }, [redoStack, gridData, rows, cols]);

  // Title editing
  const handleTitleSave = useCallback(async () => {
  if (!title.trim()) {
      setTitle(spreadsheet.name);
      setIsEditingTitle(false);
      return;
  }

  if (title !== spreadsheet.name) {
      setIsSaving(true);
      try {
    const updated = await updateSpreadsheet(spreadsheet.id, { name: title });
    if (updated) {
          // Update local state
          setTitle(title);
          // Update store if item is current
          updateCurrentItem({ name: title });
          if (onUpdate) {
      await onUpdate(spreadsheet.id, { name: title });
          }
    }
    setLastSaved(new Date());
      } catch (error) {
    handleError(error, { level: 'error', message: 'Errore nel salvataggio del titolo del foglio' });
    setTitle(spreadsheet.name);
      } finally {
    setIsSaving(false);
      }
  }
  setIsEditingTitle(false);
  }, [spreadsheet.id, spreadsheet.name, title, onUpdate, updateCurrentItem]);

  // Column headers (A, B, C, ...)
  const colHeaders = useMemo(() =>
  Array.from({ length: cols }, (_, i) => indexToColumn(i)),
  [cols]);

  return (
  <div className="flex-1 flex flex-col overflow-hidden bg-surface-primary">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border-primary bg-surface-secondary">
    <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="p-1.5 rounded-lg bg-accent-amber/10">
      <Grid3X3 className="w-4 h-4 text-accent-amber" />
          </div>

          {isEditingTitle ? (
      <div className="flex items-center gap-2 flex-1 min-w-0">
              <input
        ref={titleInputRef}
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTitleSave();
                  else if (e.key === 'Escape') {
          setTitle(spreadsheet.name);
          setIsEditingTitle(false);
                  }
        }}
        onBlur={handleTitleSave}
        className={clsx(
                  'flex-1 min-w-0 px-2 py-1 text-base font-semibold rounded',
                  'bg-surface-primary border border-border-primary text-text-primary',
                  'focus:outline-none focus:ring-2 focus:ring-accent-violet focus:border-transparent'
        )}
              />
              <button
        onClick={handleTitleSave}
        className="p-1 rounded hover:bg-surface-hover text-accent-emerald"
              >
        <Check className="w-4 h-4" />
              </button>
      </div>
          ) : (
      <button
              onClick={() => setIsEditingTitle(true)}
              className="group flex items-center gap-2 min-w-0"
      >
              <h1 className="text-base font-semibold text-text-primary truncate">
        {spreadsheet.name}
              </h1>
              <Edit3 className="w-3 h-3 text-text-quaternary opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
      </button>
          )}
    </div>

    <div className="flex items-center gap-3">
          {/* Grid size badge */}
          <div className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-md bg-surface-tertiary text-xs text-text-tertiary">
      <Table className="w-3 h-3" />
      <span>{rows} × {cols}</span>
          </div>

          {/* Save indicator */}
          <div className="flex items-center gap-2 text-xs text-text-quaternary">
      {isSaving ? (
              <>
        <Loader2 className="w-3 h-3 animate-spin" />
        <span className="hidden sm:inline">Salvataggio...</span>
              </>
      ) : lastSaved ? (
              <span className="hidden sm:inline">Salvato alle {lastSaved.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>
      ) : null}
          </div>

          {onClose && (
      <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-surface-hover transition-colors text-text-quaternary hover:text-text-secondary"
              title="Chiudi foglio"
      >
              <X className="w-4 h-4" />
      </button>
          )}
    </div>
      </div>

      {/* Toolbar */}
      <SpreadsheetToolbar
    onAddRow={handleAddRow}
    onAddColumn={handleAddColumn}
    onDeleteRow={handleDeleteRow}
    onDeleteColumn={handleDeleteColumn}
    onSortAsc={() => handleSort(true)}
    onSortDesc={() => handleSort(false)}
    onCopy={handleCopy}
    onPaste={handlePaste}
    onUndo={handleUndo}
    onRedo={handleRedo}
    canUndo={undoStack.length > 0}
    canRedo={redoStack.length > 0}
    hasSelection={selection.row !== null || selection.col !== null}
    selectedRow={selection.row}
    selectedColumn={selection.col}
      />

      {/* Formula bar */}
      <div className="flex items-center gap-2 px-2 py-1 border-b border-border-primary bg-surface-tertiary/30">
    <div className="flex items-center gap-1.5 px-1.5 py-0.5 rounded bg-surface-secondary border border-border-primary min-w-[50px] justify-center">
          <span className="text-xs font-mono font-medium text-text-tertiary">
      {selection.row !== null && selection.col !== null
              ? `${indexToColumn(selection.col)}${selection.row + 1}`
              : '—'}
          </span>
    </div>
    <div className="flex-1 flex items-center gap-1.5 px-2 py-1 rounded bg-surface-primary border border-border-primary">
          {selection.row !== null && selection.col !== null && isFormula(gridData[getCellId(selection.row, selection.col)]) && (
      <Calculator className="w-3.5 h-3.5 text-accent-blue shrink-0" />
          )}
          <span className="text-sm text-text-secondary font-mono truncate">
      {selection.row !== null && selection.col !== null
              ? (gridData[getCellId(selection.row, selection.col)] ?? '')
              : 'Seleziona una cella'}
          </span>
    </div>
      </div>

      {/* Spreadsheet Grid */}
      <div
    ref={tableRef}
    className="flex-1 overflow-auto"
    onKeyDown={handleKeyDown}
    tabIndex={0}
      >
    <div className="inline-block border-0 rounded-none overflow-hidden min-w-full">
          <table className="border-collapse">
      <thead>
              <tr>
        {/* Row number header */}
        <th className="w-10 h-7 bg-surface-tertiary border-r border-b border-border-primary sticky left-0 top-0 z-20" />
        {colHeaders.map((col, idx) => (
                  <th
          key={col}
          onClick={() => setSelection({ row: null, col: idx })}
          className={clsx(
                      'w-24 h-7 px-2 bg-surface-tertiary border-r border-b border-border-primary',
                      'text-[11px] font-semibold text-text-quaternary cursor-pointer hover:bg-surface-hover',
                      'sticky top-0 z-10 select-none',
                      selection.col === idx && 'bg-accent-violet/15 text-accent-violet'
          )}
                  >
          {col}
                  </th>
        ))}
              </tr>
      </thead>
      <tbody>
              {Array.from({ length: rows }).map((_, rowIdx) => (
        <tr key={rowIdx} className="group/row">
                  {/* Row number */}
                  <td
          onClick={() => setSelection({ row: rowIdx, col: null })}
          className={clsx(
                      'w-10 h-7 bg-surface-tertiary border-r border-b border-border-primary',
                      'text-[11px] font-medium text-text-quaternary text-center cursor-pointer',
                      'sticky left-0 z-10 select-none',
                      'group-hover/row:bg-surface-hover',
                      selection.row === rowIdx && 'bg-accent-violet/15 text-accent-violet'
          )}
                  >
          {rowIdx + 1}
                  </td>
                  {Array.from({ length: cols }).map((_, colIdx) => {
          const cellId = getCellId(rowIdx, colIdx);
          const cellValue = gridData[cellId] ?? null;
          const isSelected = selection.row === rowIdx && selection.col === colIdx;
          const isEditing = editingCell === cellId;
          const cellIsFormula = isFormula(cellValue);
          const displayVal = getDisplayValue(cellValue, gridData);

          return (
                      <td
            key={colIdx}
            onClick={() => handleCellClick(rowIdx, colIdx)}
            onDoubleClick={() => handleCellDoubleClick(rowIdx, colIdx)}
            className={clsx(
                          'w-24 h-7 border-r border-b border-border-primary relative bg-surface-primary',
                          isSelected && 'ring-2 ring-inset ring-accent-violet bg-accent-violet/5',
                          !isEditing && 'cursor-cell hover:bg-surface-hover/50'
            )}
                      >
            {isEditing ? (
                          <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              autoFocus
              className={clsx(
                              'w-full h-full px-2 text-xs font-mono absolute inset-0',
                              'bg-white text-gray-900 dark:bg-surface-primary dark:text-text-primary',
                              'focus:outline-none border-0'
              )}
                          />
            ) : (
                          <div
              className={clsx(
                              'w-full h-full px-2 flex items-center text-xs',
                              cellIsFormula ? 'text-accent-blue font-medium' : 'text-text-secondary'
              )}
                          >
              <span className="truncate">{displayVal}</span>
                          </div>
            )}
                      </td>
          );
                  })}
        </tr>
              ))}
      </tbody>
          </table>
    </div>
      </div>
  </div>
  );
}
